interface KioskSection {
    id: number;
    type?: string;
    subtitle: string;
    content?: string;
    tableData?: any;
}

interface ParseResult {
    sections: KioskSection[];
    firstImageUrl: string | null;
}

interface SessionLike {
    session_name: string;
    run_start: string;
    run_end: string;
    capacity: string;
}

interface ProgramLike {
    title: string;
    description: string;
    target: string;
    location: string;
    apply_start: string;
    apply_end: string;
    run_start: string;
    run_end: string;
    schedule_text: string;
    capacity: string;
    has_sessions: boolean;
    sessions: SessionLike[];
}

interface ImageLike {
    url: string;
    order_num: number;
}

// "2026-07-27T14:30" → "2026-07-27 14:30"
const fmtDateTime = (s: string | null | undefined): string =>
    s ? s.slice(0, 16).replace("T", " ") : "";

// 시작 ~ 종료. 둘 다 없으면 빈 문자열
const fmtPeriod = (start: string | null | undefined, end: string | null | undefined): string => {
    const a = fmtDateTime(start);
    const b = fmtDateTime(end);
    if (!a && !b) return "";
    return `${a} ~ ${b}`;
};

// 정원 0 또는 미입력 → "무제한"
const fmtCapacity = (cap: string | null | undefined): string => {
    const n = Number(cap);
    return !n || n <= 0 ? "무제한" : `${n}명`;
};

/**
 * 프로그램 관리자 폼 데이터를 키오스크 컨텐츠 섹션으로 변환한다.
 * - 컨텐츠명: 프로그램명(title) → PostRegister state.title로 전달
 * - 대표 이미지: 첫 번째 이미지
 * - 소제목(subtitle) + 내용(content) 구조. 값이 없는 항목은 제외.
 * - 회차 운영은 표(table)로 변환.
 */
export function parseProgramToKioskSections(
    program: ProgramLike,
    images: ImageLike[],
): ParseResult {
    const sections: KioskSection[] = [];
    let idCounter = Date.now();

    const pushText = (subtitle: string, content: string) => {
        sections.push({ id: idCounter++, subtitle, content });
    };

    // 대상 / 장소 (없으면 제외)
    if (program.target?.trim()) pushText("대상", program.target.trim());
    if (program.location?.trim()) pushText("장소", program.location.trim());

    // 신청기간 (없으면 제외)
    const applyPeriod = fmtPeriod(program.apply_start, program.apply_end);
    if (applyPeriod) pushText("신청기간", applyPeriod);

    // 운영일정 (없으면 제외)
    const runPeriod = fmtPeriod(program.run_start, program.run_end);
    if (runPeriod) pushText("운영일정", runPeriod);

    // 회차 운영 → 표 (회차명 | 운영기간 | 정원)
    if (program.has_sessions && program.sessions?.length) {
        sections.push({
            id: idCounter++,
            type: "table",
            subtitle: "회차 운영",
            tableData: {
                tableType: "text",
                headers: ["회차명", "운영기간", "정원"],
                columnKeys: ["session_name", "run_period", "capacity"],
                rows: program.sessions.map((s) => ({
                    cells: [
                        { cellType: "text", value: s.session_name || "" },
                        { cellType: "text", value: fmtPeriod(s.run_start, s.run_end) },
                        { cellType: "text", value: fmtCapacity(s.capacity) },
                    ],
                })),
            },
        });
    }

    // 정원 (0이면 무제한)
    pushText("정원", fmtCapacity(program.capacity));

    // 운영안내문구 (없으면 제외)
    if (program.schedule_text?.trim()) pushText("운영안내문구", program.schedule_text.trim());

    // 프로그램 설명 (없으면 제외)
    if (program.description?.trim()) pushText("프로그램 설명", program.description.trim());

    // 최소 한 개의 섹션 보장
    if (sections.length === 0) {
        sections.push({ id: idCounter++, subtitle: "", content: "" });
    }

    const firstImageUrl = images?.[0]?.url ?? null;
    return { sections, firstImageUrl };
}
